class SolutionAdvisor extends HTMLElement {
  static ROLES = {
    developer: { label: 'Developer / Programmer', baseRam: 8, defaultTools: ['cursor', 'vscode', 'chrome_heavy', 'docker', 'teams'] },
    designer: { label: 'Designer / Creative', baseRam: 8, defaultTools: ['figma', 'photoshop', 'chrome_heavy', 'teams'] },
    video_editor: { label: 'Video Editor', baseRam: 12, defaultTools: ['premiere', 'after_effects', 'chrome_heavy', 'teams'] },
    finance: { label: 'Finance / Professional', baseRam: 6, defaultTools: ['excel', 'outlook', 'teams', 'chrome_heavy'] },
    security: { label: 'Security / Analyst', baseRam: 10, defaultTools: ['splunk', 'wireshark', 'chrome_heavy', 'teams', 'terminal'] },
    it_admin: { label: 'IT / System Admin', baseRam: 10, defaultTools: ['vmware', 'terminal', 'chrome_heavy', 'teams'] },
    student: { label: 'Student', baseRam: 6, defaultTools: ['vscode', 'chrome_heavy', 'teams'] },
  };

  static TOOLS = {
    cursor: { label: 'Cursor', ram: 3, gpu: 0 },
    vscode: { label: 'VS Code', ram: 2, gpu: 0 },
    chrome_heavy: { label: 'Chrome (30+ tabs)', ram: 5, gpu: 0 },
    docker: { label: 'Docker', ram: 6, gpu: 0 },
    terminal: { label: 'Terminal / CLI', ram: 1, gpu: 0 },
    teams: { label: 'Microsoft Teams', ram: 2, gpu: 0 },
    slack: { label: 'Slack', ram: 2, gpu: 0 },
    figma: { label: 'Figma', ram: 3, gpu: 1 },
    photoshop: { label: 'Photoshop', ram: 5, gpu: 2 },
    illustrator: { label: 'Illustrator', ram: 3, gpu: 1 },
    after_effects: { label: 'After Effects', ram: 8, gpu: 4 },
    premiere: { label: 'Premiere Pro', ram: 6, gpu: 4 },
    excel: { label: 'Excel / Sheets', ram: 2, gpu: 0 },
    vmware: { label: 'VMware / VMs', ram: 6, gpu: 0 },
    local_ai: { label: 'Local AI / LLM', ram: 8, gpu: 5 },
    local_db: { label: 'Local database', ram: 3, gpu: 0 },
    wireshark: { label: 'Wireshark', ram: 2, gpu: 0 },
    splunk: { label: 'Splunk / SIEM', ram: 4, gpu: 0 },
    outlook: { label: 'Outlook', ram: 2, gpu: 0 },
    spotify: { label: 'Spotify / background apps', ram: 1, gpu: 0 },
  };

  connectedCallback() {
    this.step = 1;
    this.selectedProduct = null;
    this.selectedAccessoryIds = new Set();
    this.recommendations = [];
    this.moneyFormat = this.dataset.moneyFormat || '{{amount}}';
    this.contactUrl = this.dataset.contactUrl || '/pages/contact-us';

    this.catalog = this.parseCatalog();
    this.panels = [...this.querySelectorAll('[data-advisor-panel]')];
    this.stepIndicators = [...this.querySelectorAll('[data-step-indicator]')];
    this.nextBtn = this.querySelector('[data-advisor-next]');
    this.backBtn = this.querySelector('[data-advisor-back]');
    this.resultsEl = this.querySelector('[data-advisor-results]');
    this.resultsIntroEl = this.querySelector('[data-advisor-results-intro]');
    this.emptyEl = this.querySelector('[data-advisor-empty]');
    this.summaryEl = this.querySelector('[data-advisor-summary]');
    this.totalsEl = this.querySelector('[data-advisor-totals]');
    this.accessoryCountEl = this.querySelector('[data-advisor-accessory-count]');
    this.accessoryListEl = this.querySelector('[data-advisor-accessory-list]');
    this.accessoryMoreEl = this.querySelector('[data-advisor-accessory-more]');
    this.resultTemplate = document.getElementById('solution-advisor-result-template');
    this.addCartBtn = this.querySelector('[data-advisor-add-cart]');
    this.quoteLink = this.querySelector('[data-advisor-quote]');
    this.summaryRoleEl = this.querySelector('[data-summary-role]');
    this.summaryToolsEl = this.querySelector('[data-summary-tools]');
    this.summaryRamEl = this.querySelector('[data-summary-ram]');
    this.summaryBudgetEl = this.querySelector('[data-summary-budget]');

    this.bindEvents();
    this.applyRoleDefaults(false);
    this.updateWorkflowSummary();
    this.updateNav();
  }

  parseCatalog() {
    const script = this.querySelector('[data-advisor-catalog]');
    if (!script) return [];
    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.error('Solution advisor catalog parse failed', error);
      return [];
    }
  }

  bindEvents() {
    this.nextBtn?.addEventListener('click', () => this.goNext());
    this.backBtn?.addEventListener('click', () => this.goBack());
    this.addCartBtn?.addEventListener('click', () => this.addToCart());

    this.querySelectorAll('input[name="role"]').forEach((input) => {
      input.addEventListener('change', () => {
        this.applyRoleDefaults(true);
        this.updateWorkflowSummary();
      });
    });

    this.querySelectorAll('input[name="category"], input[name="tool"], select[name]').forEach((input) => {
      input.addEventListener('change', () => this.updateWorkflowSummary());
    });

    this.querySelector('[name="notes"]')?.addEventListener('input', () => this.updateWorkflowSummary());

    document.querySelectorAll('[data-advisor-category]').forEach((button) => {
      button.addEventListener('click', () => {
        const value = button.dataset.advisorCategory;
        const radio = this.querySelector(`input[name="category"][value="${value}"]`);
        if (radio) {
          radio.checked = true;
          this.updateWorkflowSummary();
        }
        document.getElementById('advisor-wizard')?.scrollIntoView({ behavior: 'smooth' });
      });
    });
  }

  applyRoleDefaults(clearFirst) {
    const role = this.getRole();
    const defaults = SolutionAdvisor.ROLES[role]?.defaultTools || [];
    if (clearFirst) {
      this.querySelectorAll('input[name="tool"]').forEach((input) => {
        input.checked = false;
      });
    }
    defaults.forEach((tool) => {
      const input = this.querySelector(`input[name="tool"][value="${tool}"]`);
      if (input) input.checked = true;
    });
  }

  getRole() {
    return this.querySelector('input[name="role"]:checked')?.value || 'developer';
  }

  getCategory() {
    return this.querySelector('input[name="category"]:checked')?.value || 'laptop';
  }

  getTools() {
    return [...this.querySelectorAll('input[name="tool"]:checked')].map((el) => el.value);
  }

  getAnswers() {
    return {
      role: this.getRole(),
      category: this.getCategory(),
      tools: this.getTools(),
      monitors: this.querySelector('[name="monitors"]')?.value || '2',
      travel: this.querySelector('[name="travel"]')?.value || 'rarely',
      localAi: this.querySelector('[name="local_ai"]')?.value || 'no',
      video4k: this.querySelector('[name="video_4k"]')?.value || 'no',
      budget: this.querySelector('[name="budget"]')?.value || '100000-150000',
      brand: this.querySelector('[name="brand"]')?.value || 'any',
      notes: this.querySelector('[name="notes"]')?.value?.trim() || '',
    };
  }

  getToolLabels(tools) {
    return tools.map((slug) => SolutionAdvisor.TOOLS[slug]?.label || slug);
  }

  estimateWorkload(answers) {
    const role = SolutionAdvisor.ROLES[answers.role] || SolutionAdvisor.ROLES.developer;
    let ram = role.baseRam;
    let gpuNeed = 0;
    const toolLabels = [];

    answers.tools.forEach((slug) => {
      const tool = SolutionAdvisor.TOOLS[slug];
      if (!tool) return;
      ram += tool.ram;
      gpuNeed += tool.gpu;
      toolLabels.push(tool.label);
    });

    const monitors = Number(answers.monitors) || 1;
    if (monitors > 1) ram += (monitors - 1) * 2;

    if (answers.localAi === 'yes') {
      ram += 8;
      gpuNeed += 4;
    } else if (answers.localAi === 'maybe') {
      ram += 4;
      gpuNeed += 2;
    }

    if (answers.video4k === 'yes') {
      ram += 8;
      gpuNeed += 5;
    } else if (answers.video4k === 'sometimes') {
      ram += 4;
      gpuNeed += 2;
    }

    if (answers.travel === 'often' && answers.category === 'laptop') {
      ram += 2;
    }

    const recommendedRam = ram <= 12 ? 16 : ram <= 20 ? 32 : ram <= 36 ? 32 : 64;
    const headroomRam = Math.max(recommendedRam, Math.ceil(ram * 1.25 / 8) * 8);

    return {
      estimatedUsage: ram,
      recommendedRam: headroomRam,
      gpuNeed,
      toolLabels,
      roleLabel: role.label,
    };
  }

  updateWorkflowSummary() {
    const answers = this.getAnswers();
    const workload = this.estimateWorkload(answers);
    const budgetLabel = this.querySelector('[name="budget"] option:checked')?.textContent || answers.budget;

    if (this.summaryRoleEl) this.summaryRoleEl.textContent = workload.roleLabel;
    if (this.summaryToolsEl) {
      this.summaryToolsEl.textContent =
        workload.toolLabels.length > 0 ? workload.toolLabels.slice(0, 5).join(', ') + (workload.toolLabels.length > 5 ? '…' : '') : 'Select your daily apps';
    }
    if (this.summaryRamEl) {
      this.summaryRamEl.textContent = `~${workload.estimatedUsage}GB running together → ${workload.recommendedRam}GB recommended`;
    }
    if (this.summaryBudgetEl) this.summaryBudgetEl.textContent = budgetLabel;
  }

  parseRam(value) {
    const match = String(value || '').match(/(\d+)\s*GB/i);
    return match ? Number(match[1]) : 8;
  }

  parseStorageGB(value) {
    const match = String(value || '').match(/(\d+)\s*GB/i);
    if (match) return Number(match[1]);
    const tb = String(value || '').match(/(\d+)\s*TB/i);
    return tb ? Number(tb[1]) * 1024 : 256;
  }

  parseCpuTier(cpu) {
    const value = String(cpu || '').toLowerCase();
    if (/(i9|ryzen 9|ultra 9|xeon|epyc)/.test(value)) return 5;
    if (/(i7|ryzen 7|ultra 7|core 7)/.test(value)) return 4;
    if (/(i5|ryzen 5|ultra 5|core 5)/.test(value)) return 3;
    if (/(i3|ryzen 3|celeron|pentium)/.test(value)) return 1;
    return 2;
  }

  hasDedicatedGpu(product) {
    const gpu = String(product.gpu || '').toLowerCase();
    return gpu && !gpu.includes('integrated') && gpu !== 'n/a' && gpu !== '' && !gpu.includes('uhd');
  }

  budgetRange(budgetKey) {
    const map = {
      '0-50000': [0, 5000000],
      '50000-80000': [5000000, 8000000],
      '80000-120000': [8000000, 12000000],
      '100000-150000': [10000000, 15000000],
      '120000-200000': [12000000, 20000000],
      '200000+': [20000000, Number.MAX_SAFE_INTEGER],
    };
    return map[budgetKey] || [0, Number.MAX_SAFE_INTEGER];
  }

  normalizeTags(tags) {
    return (tags || []).map((tag) =>
      String(tag)
        .trim()
        .toLowerCase()
        .replace(/[\s-]+/g, '_')
    );
  }

  generateWorkflowReasons(product, answers, workload) {
    const reasons = [];
    const ram = this.parseRam(product.ram);
    const storage = this.parseStorageGB(product.storage);
    const dedicatedGpu = this.hasDedicatedGpu(product);
    const tools = answers.tools;

    if (ram >= workload.recommendedRam) {
      const heavy = tools.filter((t) => ['docker', 'chrome_heavy', 'cursor', 'local_ai', 'vmware'].includes(t));
      if (heavy.length >= 2) {
        const names = heavy.slice(0, 3).map((t) => SolutionAdvisor.TOOLS[t]?.label.split(' ')[0] || t);
        reasons.push(`Enough RAM for ${names.join(', ')} running together`);
      } else {
        reasons.push(`Headroom for your full workday (~${workload.estimatedUsage}GB in use)`);
      }
    } else if (ram >= workload.estimatedUsage) {
      reasons.push('Covers your current workload with moderate headroom');
    } else {
      reasons.push('May feel tight when everything is open — consider more RAM');
    }

    if (dedicatedGpu && (workload.gpuNeed >= 4 || answers.localAi === 'yes')) {
      if (answers.localAi === 'yes' || tools.includes('local_ai')) {
        reasons.push('Dedicated GPU accelerates local AI models');
      } else if (tools.some((t) => ['after_effects', 'premiere', 'photoshop'].includes(t))) {
        reasons.push('GPU acceleration for creative and video workloads');
      } else {
        reasons.push('Dedicated GPU keeps graphics-heavy apps smooth');
      }
    }

    if (Number(answers.monitors) >= 2) {
      reasons.push(`Supports ${answers.monitors === '4' ? '4+' : answers.monitors} display workflow`);
    }

    if (storage >= 1024) {
      reasons.push(`${product.storage || 'Large SSD'} leaves room for project datasets`);
    } else if (storage >= 512) {
      reasons.push('512GB+ storage for projects and local tools');
    }

    if (answers.travel === 'often' && product.category === 'laptop') {
      reasons.push('Portable enough for frequent travel');
    }

    const tags = this.normalizeTags(product.workloadTags);
    if (tags.length && tools.some((t) => tags.includes(t) || tags.includes(answers.role))) {
      reasons.push('Verified against similar real-world workloads');
    }

    return [...new Set(reasons)].slice(0, 4);
  }

  scoreProduct(product, answers, workload) {
    if (product.category !== answers.category) return null;

    let score = 0;
    const reasons = [];
    const [minPrice, maxPrice] = this.budgetRange(answers.budget);
    const inBudget = product.price >= minPrice && product.price <= maxPrice;

    if (inBudget) score += 40;
    else if (product.price < minPrice) score += 8;
    else score -= 30;

    if (answers.brand !== 'any' && product.vendor.toLowerCase() === answers.brand.toLowerCase()) {
      score += 18;
    } else if (answers.brand !== 'any') {
      score -= 12;
    }

    const ram = this.parseRam(product.ram);
    const storage = this.parseStorageGB(product.storage);
    const dedicatedGpu = this.hasDedicatedGpu(product);
    const cpuTier = this.parseCpuTier(product.cpu);

    const ramDelta = ram - workload.recommendedRam;
    if (ramDelta >= 0) score += 35 + Math.min(ramDelta, 16);
    else score += ramDelta * 4;

    if (workload.gpuNeed >= 4) {
      if (dedicatedGpu) score += 28;
      else score -= 18;
    } else if (workload.gpuNeed >= 2 && dedicatedGpu) {
      score += 14;
    }

    score += cpuTier * 4;

    if (answers.tools.includes('docker') || answers.tools.includes('vmware')) {
      score += cpuTier >= 3 ? 10 : -8;
    }

    if (storage >= 512) score += 8;
    if (storage >= 1024) score += 6;

    const tags = this.normalizeTags(product.workloadTags);
    if (tags.includes(answers.role)) score += 20;
    answers.tools.forEach((tool) => {
      if (tags.includes(tool)) score += 12;
    });

    const workflowReasons = this.generateWorkflowReasons(product, answers, workload);
    reasons.push(...workflowReasons);

    return {
      product,
      score,
      reasons: [...new Set(reasons)].slice(0, 4),
      inBudget,
    };
  }

  buildRecommendations() {
    const answers = this.getAnswers();
    const workload = this.estimateWorkload(answers);
    const scored = this.catalog
      .map((product) => this.scoreProduct(product, answers, workload))
      .filter(Boolean)
      .sort((a, b) => b.score - a.score);

    const inBudget = scored.filter((item) => item.inBudget);
    const pool = inBudget.length ? inBudget : scored;
    return { recommendations: pool.slice(0, 3), workload, answers };
  }

  formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, this.moneyFormat);
    }
    return `₹${(cents / 100).toLocaleString('en-IN')}`;
  }

  getProductAccessories(product) {
    return Array.isArray(product?.accessories) ? product.accessories.filter((item) => item.variantId) : [];
  }

  getAccessoryDisplayCount(product) {
    const linked = this.getProductAccessories(product).length;
    const override = Number(product.accessoryCount);
    if (override > 0) return override;
    return linked;
  }

  getSelectedAccessories() {
    const accessories = this.getProductAccessories(this.selectedProduct);
    return accessories.filter((item) => this.selectedAccessoryIds.has(String(item.variantId)));
  }

  renderRecommendations() {
    const { recommendations, workload, answers } = this.buildRecommendations();
    this.recommendations = recommendations;
    this.resultsEl.innerHTML = '';
    this.emptyEl.hidden = this.recommendations.length > 0;

    if (this.resultsIntroEl) {
      if (this.recommendations.length) {
        this.resultsIntroEl.hidden = false;
        this.resultsIntroEl.textContent = `Based on your workflow as a ${workload.roleLabel.toLowerCase()} with ${answers.tools.length} apps running together, we recommend systems with at least ${workload.recommendedRam}GB RAM.`;
      } else {
        this.resultsIntroEl.hidden = true;
      }
    }

    this.recommendations.forEach((entry, index) => {
      const node = this.resultTemplate.content.cloneNode(true);
      const badge = node.querySelector('[data-result-badge]');
      badge.textContent = index === 0 ? 'Recommended' : index === 1 ? 'Great Performance' : 'Good Fit';
      badge.classList.add(index === 0 ? 'is-best' : 'is-good');

      const img = node.querySelector('[data-result-image]');
      if (entry.product.image) {
        img.src = entry.product.image;
        img.alt = entry.product.title;
      } else {
        img.hidden = true;
      }

      node.querySelector('[data-result-title]').textContent = entry.product.title;
      node.querySelector('[data-result-price]').textContent = this.formatMoney(entry.product.price);

      const specs = node.querySelector('[data-result-specs]');
      [entry.product.cpu, entry.product.ram, entry.product.storage, entry.product.gpu]
        .filter(Boolean)
        .forEach((spec) => {
          const li = document.createElement('li');
          li.textContent = spec;
          specs.appendChild(li);
        });

      const reasons = node.querySelector('[data-result-reasons]');
      entry.reasons.slice(0, 4).forEach((reason) => {
        const li = document.createElement('li');
        li.textContent = reason;
        reasons.appendChild(li);
      });

      node.querySelector('[data-result-select]').addEventListener('click', () => {
        this.selectedProduct = entry.product;
        this.step = 4;
        this.showStep();
        this.renderSummary();
      });

      this.resultsEl.appendChild(node);
    });

    if (this.recommendations.length && !this.selectedProduct) {
      this.selectedProduct = this.recommendations[0].product;
    }
  }

  renderSummary() {
    const product = this.selectedProduct;
    if (!product || !this.summaryEl) return;

    const accessories = this.getProductAccessories(product);
    this.selectedAccessoryIds = new Set(accessories.map((item) => String(item.variantId)));

    this.summaryEl.innerHTML = `
      <h3>Complete your setup</h3>
      <div class="solution-advisor__summary-card">
        ${product.image ? `<img src="${product.image}" alt="${product.title}" width="96" height="96">` : ''}
        <div>
          <strong>${product.title}</strong>
          <p>${this.formatMoney(product.price)}</p>
          <a href="${product.url}#setup-builder">Customize full accessory list on product page →</a>
        </div>
      </div>
    `;

    const displayCount = this.getAccessoryDisplayCount(product);
    if (this.accessoryCountEl) {
      this.accessoryCountEl.textContent =
        accessories.length > 0
          ? `We matched ${accessories.length} verified accessories to this product`
          : displayCount > 0
            ? `We found ${displayCount} compatible accessories for this product`
            : 'Browse compatible accessories on the product page';
    }

    if (this.accessoryListEl) {
      this.accessoryListEl.innerHTML = '';
      accessories.forEach((accessory) => {
        const li = document.createElement('li');
        li.className = 'solution-advisor__accessory-item';
        li.innerHTML = `
          <label>
            <input type="checkbox" data-accessory-id="${accessory.variantId}" checked>
            ${accessory.image ? `<img src="${accessory.image}" alt="" width="40" height="40">` : ''}
            <span>
              <strong>${accessory.title}</strong>
              <small>${accessory.port}${accessory.note ? ` · ${accessory.note}` : ''}</small>
              <small>${this.formatMoney(accessory.price)}</small>
            </span>
          </label>
        `;
        li.querySelector('input').addEventListener('change', (event) => {
          const id = String(accessory.variantId);
          if (event.target.checked) this.selectedAccessoryIds.add(id);
          else this.selectedAccessoryIds.delete(id);
          this.updateTotals();
          this.updateQuoteLink();
        });
        this.accessoryListEl.appendChild(li);
      });
    }

    if (this.accessoryMoreEl) {
      const extra = displayCount - accessories.length;
      if (extra > 0) {
        this.accessoryMoreEl.hidden = false;
        this.accessoryMoreEl.innerHTML = `<a href="${product.url}#setup-builder">View ${extra} more compatible accessories →</a>`;
      } else {
        this.accessoryMoreEl.hidden = true;
      }
    }

    this.updateTotals();
    this.updateQuoteLink();
  }

  updateTotals() {
    const product = this.selectedProduct;
    if (!product || !this.totalsEl) return;

    const selected = this.getSelectedAccessories();
    const accessoriesTotal = selected.reduce((sum, item) => sum + item.price, 0);
    const grandTotal = product.price + accessoriesTotal;

    this.totalsEl.innerHTML = `
      <div><dt>Hardware</dt><dd>${this.formatMoney(product.price)}</dd></div>
      <div><dt>Accessories (${selected.length})</dt><dd>${accessoriesTotal ? this.formatMoney(accessoriesTotal) : 'None selected'}</dd></div>
      <div class="is-total"><dt>Estimated total</dt><dd>${this.formatMoney(grandTotal)}</dd></div>
    `;
  }

  updateQuoteLink() {
    if (!this.quoteLink) return;
    const notes = encodeURIComponent(this.buildQuoteSummary());
    this.quoteLink.href = `${this.contactUrl}${this.contactUrl.includes('?') ? '&' : '?'}subject=${encodeURIComponent('Workflow advisor quote')}&body=${notes}`;
  }

  buildQuoteSummary() {
    const answers = this.getAnswers();
    const workload = this.estimateWorkload(answers);
    const product = this.selectedProduct;
    const accessories = this.getSelectedAccessories();
    return [
      'Hi Blueberry team,',
      '',
      'I used the Workflow Advisor and would like a quote.',
      '',
      `Role: ${workload.roleLabel}`,
      `Device: ${answers.category}`,
      `Tools open daily: ${this.getToolLabels(answers.tools).join(', ') || 'Not specified'}`,
      `Displays: ${answers.monitors}`,
      `Travel: ${answers.travel}`,
      `Local AI: ${answers.localAi}`,
      `4K editing: ${answers.video4k}`,
      `Estimated RAM need: ~${workload.estimatedUsage}GB → ${workload.recommendedRam}GB recommended`,
      `Budget: ${answers.budget}`,
      `Brand: ${answers.brand}`,
      answers.notes ? `Notes: ${answers.notes}` : '',
      product ? `Selected product: ${product.title}` : '',
      accessories.length ? `Accessories: ${accessories.map((item) => item.title).join(', ')}` : '',
    ]
      .filter(Boolean)
      .join('\n');
  }

  validateStep() {
    if (this.step === 1 && !this.getRole()) {
      window.alert('Tell us what you do so we can model your workday.');
      return false;
    }
    if (this.step === 2 && this.getTools().length === 0) {
      window.alert('Select at least one app that stays open during your day.');
      return false;
    }
    return true;
  }

  goNext() {
    if (!this.validateStep()) return;

    if (this.step === 2) {
      this.renderRecommendations();
    }

    if (this.step === 3) {
      if (!this.selectedProduct && this.recommendations[0]) {
        this.selectedProduct = this.recommendations[0].product;
      }
      this.renderSummary();
    }

    if (this.step < 4) {
      this.step += 1;
      this.showStep();
    }
  }

  goBack() {
    if (this.step > 1) {
      this.step -= 1;
      this.showStep();
    }
  }

  showStep() {
    this.panels.forEach((panel) => {
      panel.hidden = Number(panel.dataset.advisorPanel) !== this.step;
    });

    this.stepIndicators.forEach((indicator) => {
      const stepNum = Number(indicator.dataset.stepIndicator);
      indicator.classList.toggle('is-active', stepNum === this.step);
      indicator.classList.toggle('is-complete', stepNum < this.step);
    });

    this.backBtn.hidden = this.step === 1;
    this.nextBtn.textContent =
      this.step === 2 ? 'Get recommendations' : this.step === 3 ? 'Continue to setup' : this.step === 4 ? 'Done' : 'Continue';
    this.nextBtn.hidden = this.step === 4;
    this.updateNav();
  }

  updateNav() {
    if (this.nextBtn) {
      this.nextBtn.disabled = this.step === 3 && this.recommendations.length === 0;
    }
  }

  async addToCart() {
    if (!this.selectedProduct) return;

    this.addCartBtn.classList.add('loading');
    this.addCartBtn.disabled = true;

    const cartAddUrl = window.routes?.cart_add_url || `${window.Shopify?.routes?.root || '/'}cart/add.js`;
    const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    const queue = [
      {
        id: this.selectedProduct.variantId,
        quantity: 1,
        properties: { 'Selected via': 'Workflow Advisor' },
      },
      ...this.getSelectedAccessories().map((item) => ({
        id: item.variantId,
        quantity: 1,
        properties: { 'Added for': this.selectedProduct.title },
      })),
    ];

    try {
      let lastResponse = null;

      for (let i = 0; i < queue.length; i += 1) {
        const entry = queue[i];
        const formData = new FormData();
        formData.append('id', entry.id);
        formData.append('quantity', String(entry.quantity));
        Object.entries(entry.properties || {}).forEach(([key, value]) => {
          formData.append(`properties[${key}]`, value);
        });

        if (cart && i === queue.length - 1) {
          formData.append('sections', cart.getSectionsToRender().map((section) => section.id).join(','));
          formData.append('sections_url', window.location.pathname);
        }

        const config = typeof fetchConfig === 'function' ? fetchConfig('javascript') : { method: 'POST' };
        config.headers = config.headers || {};
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = formData;

        const response = await fetch(cartAddUrl, config);
        lastResponse = await response.json();
        if (lastResponse.status) throw new Error(lastResponse.description || lastResponse.message);
      }

      if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'solution-advisor', cartData: lastResponse });
      }

      if (cart && lastResponse?.sections) {
        cart.renderContents(lastResponse);
      } else {
        window.location.href = window.routes?.cart_url || '/cart';
      }
    } catch (error) {
      console.error(error);
      window.alert('Could not add to cart. Please try again or contact us for help.');
    } finally {
      this.addCartBtn.classList.remove('loading');
      this.addCartBtn.disabled = false;
    }
  }
}

customElements.define('solution-advisor', SolutionAdvisor);
